import { ChangeDetectorRef, Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { PortalService } from "../services/portal-service.service";
import { Contracts } from "../models/contracts";
import { LogService } from "../services/log.service";
import { LicenseOverviewComponent } from "./license-overview/license-overview.component";
import { LicenseActivationComponent } from "./license-activation/license-activation.component";
import { LicenseSurrenderComponent } from "./license-surrender/license-surrender.component";
import { OfflineSurrenderComponent } from "./offline-surrender/offline-surrender.component";
import { TokenRequestComponent } from "./token-request/token-request.component";
import { DownloadsComponent } from "../downloads/downloads.component";
import { ActivityTabComponent } from "./activity/activity-tab.component";
import { ShellService } from "../services/shell.service";
import { BrowserStorage } from "../core/storage";

type PortalTab = "view" | "activate" | "surrender" | "offline-surrender" | "request-tokens" | "activity";

@Component({
  standalone: false,
  selector: "app-manage-licenses",
  templateUrl: "./manageLicenses.component.html",
  styleUrls: ["./manageLicenses.component.scss"],
})
export class ManageLicensesComponent implements OnInit {
  activetab = 1;
  contracts: Contracts[] = [];
  contract: Contracts | null = null;
  disableTokenTab = false;
  disableActTab = false;
  disableSurrenderTab = false;
  loading = true;
  showActivationCodes = false;
  showComments = false;

  @ViewChild("viewTab", { static: false }) viewTab: LicenseOverviewComponent;
  @ViewChild("actTab", { static: false }) actTab: LicenseActivationComponent;
  @ViewChild("surrenderTab", { static: false })
  surrenderTab: LicenseSurrenderComponent;
  @ViewChild("offlineTab", { static: false })
  offlineTab: OfflineSurrenderComponent;
  @ViewChild("tokenTab", { static: false }) tokenTab: TokenRequestComponent;
  @ViewChild("activityTab", { static: false })
  activityTab: ActivityTabComponent;
  @ViewChild("downloadTab", { static: false }) downloadTab: DownloadsComponent;

  constructor(
    private portalService: PortalService,
    private logService: LogService,
    private cd: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    public shellService: ShellService,
  ) {}

  ngOnInit() {
    this.showActivationCodes = BrowserStorage.getBoolean("activation-show-codes", false, false) ?? false;
    this.showComments = BrowserStorage.getBoolean("activation-show-comments", false, false) ?? false;

    this.route.queryParamMap.subscribe((params) => {
      this.applyTab((params.get("tab") as PortalTab) || "view");
      const contractId = Number(params.get("contract"));
      if (contractId && this.contracts.length) this.selectContract(contractId);
    });

    this.getContracts();
  }

  private applyTab(tab: PortalTab): void {
    const tabs: Record<PortalTab, number> = {
      view: 1,
      activate: 2,
      surrender: 3,
      "request-tokens": 4,
      activity: 5,
      "offline-surrender": 6,
    };

    this.activetab = tabs[tab] || 1;
  }

  private selectContract(contractId: number): void {
    const selected = this.contracts.find((item) => item.id === contractId);
    if (selected) {
      this.contract = selected;
      this.setContract(false);
    }
  }

  setContract(updateUrl = true) {
    if (!this.contract) return;

    this.disableTokenTab = this.portalService.disableTokenTab(this.contract);
    this.disableActTab = this.portalService.disableActivateTab(this.contract);
    this.disableSurrenderTab = this.portalService.disableSurrenderTab(this.contract);

    if (updateUrl) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { contract: this.contract.id },
        queryParamsHandling: "merge",
      });
    }

    this.cd.detectChanges();
    this.viewTab?.ngOnInit();
    this.actTab?.ngOnInit();
    this.surrenderTab?.ngOnInit();
    this.tokenTab?.ngOnInit();
    this.activityTab?.ngOnInit();
    this.downloadTab?.refresh(true);
  }

  getContracts() {
    this.loading = true;
    return this.portalService.GetContracts().subscribe(
      (data: any[]) => {
        this.contracts = data as Contracts[];
        const requested = Number(this.route.snapshot.queryParamMap.get("contract"));
        this.contract =
          this.contracts.find((item) => item.id === requested) || this.shellService.selectedContract || this.contracts[0] || null;
        if (this.contract) this.shellService.setSelectedContract(this.contract.id);
        this.setContract(false);
        this.loading = false;
      },
      (error) => {
        this.logService.log("Failed to get contracts", error);
        this.loading = false;
      },
    );
  }

  get pageTitle(): string {
    switch (this.activetab) {
      case 2:
        return "Activate Licenses";
      case 3:
        return "Surrender Licenses";
      case 4:
        return "Request Tokens";
      case 5:
        return "Activity Log";
      case 6:
        return "Offline Surrender";
      default:
        return "View Licenses";
    }
  }

  refresh(): void {
    this.getContracts();
  }

  toggleActivationCodes(): void {
    this.showActivationCodes = !this.showActivationCodes;
    BrowserStorage.setBoolean("activation-show-codes", this.showActivationCodes, false);
  }

  toggleComments(): void {
    this.showComments = !this.showComments;
    BrowserStorage.setBoolean("activation-show-comments", this.showComments, false);
  }
}
